/*
 * Dreamy — galerie produit : défilement natif (scroll-snap, inertie du système, scroll-snap-stop: always),
 * points indicateurs (non cliquables : cibles tactiles ≥ 48 px impossibles pour 7 points), flèches du clavier sur la
 * piste (focusable), position annoncée aux lecteurs d'écran (« Visuel 2 sur 7 »).
 * Flèches précédent / suivant sur la photo (mobile et bureau) et miniatures cliquables (bureau), DECISIONS D91.
 * À la souris, chaque moitié de la photo est cliquable (précédent / suivant) avec un curseur flèche.
 * Motion (prompt 3, DECISIONS D81) : le point actif s'allonge en pilule et suit le doigt en continu (--p de 0 à 1 par
 * point, interpolé entre deux visuels) ; indice de geste unique par session (la piste glisse de 24 px puis revient),
 * seulement si la galerie n'a pas encore été touchée, jamais en reduced-motion ni dans l'éditeur.
 */
if (!customElements.get('dreamy-gallery')) {
  customElements.define(
    'dreamy-gallery',
    class DreamyGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.dots = Array.prototype.slice.call(this.querySelectorAll('.dr-gallery__dot'));
        this.dotsWrap = this.querySelector('.dr-gallery__dots');
        this.status = this.querySelector('[data-gallery-status]');
        this.last = 0;
        this.touched = false;
        if (!this.track || !this.dots.length) return;
        var ticking = false;
        var idle;
        this.track.addEventListener(
          'scroll',
          () => {
            // Pendant le geste, la pilule suit le défilement sans transition (pas de retard sur le doigt).
            this.dotsWrap.classList.add('is-live');
            clearTimeout(idle);
            idle = setTimeout(() => this.dotsWrap.classList.remove('is-live'), 160);
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              this.sync();
              ticking = false;
            });
          },
          { passive: true }
        );
        this.track.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          this.go(this.index() + (e.key === 'ArrowRight' ? 1 : -1));
        });
        var touch = () => {
          this.touched = true;
          this.track.classList.remove('is-hint');
          this.remember();
        };
        // Visuels 2 et suivants (<noscript data-dreamy-defer>) : insérés au chargement complet ou au premier geste.
        var hydrate = () => {
          Array.prototype.forEach.call(this.querySelectorAll('noscript[data-dreamy-defer]'), (n) => {
            n.insertAdjacentHTML('afterend', n.textContent);
            n.remove();
          });
        };
        // Dès que le 1er visuel est chargé (sans concurrencer le LCP) plutôt qu'au chargement complet de la page :
        // sur réseau lent, les visuels suivants sont en place avant le premier geste. Insérés pendant un balayage,
        // Safari recalerait la galerie sur le 1er visuel (vu dans le simulateur iPhone 17, D93).
        var first = this.track.querySelector('img');
        if (document.readyState === 'complete' || !first || (first.complete && first.naturalWidth)) hydrate();
        else {
          first.addEventListener('load', hydrate, { once: true });
          first.addEventListener('error', hydrate, { once: true });
          window.addEventListener('load', hydrate, { once: true });
        }
        ['pointerdown', 'wheel', 'keydown', 'focusin'].forEach((t) => this.track.addEventListener(t, hydrate, { once: true, passive: true }));
        this.track.addEventListener('pointerdown', touch, { passive: true });
        this.track.addEventListener('wheel', touch, { passive: true });
        this.track.addEventListener('keydown', touch);
        // Flèches (mobile et bureau) et miniatures (bureau) : aria-disabled aux extrémités (le focus reste sur le bouton).
        this.prev = this.querySelector('[data-gallery-prev]');
        this.next = this.querySelector('[data-gallery-next]');
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));
        var nav = (i) => {
          hydrate();
          touch();
          this.go(i);
        };
        if (this.prev) this.prev.addEventListener('click', () => nav(this.index() - 1));
        if (this.next) this.next.addEventListener('click', () => nav(this.index() + 1));
        this.thumbs.forEach((t, j) => t.addEventListener('click', () => nav(j)));
        // Souris (bureau) : moitié gauche = visuel précédent, moitié droite = suivant. Le curseur flèche l'annonce,
        // un clic y va ; aux extrémités, curseur normal et clic sans effet.
        if (this.dots.length > 1 && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.track.addEventListener('mousemove', (e) => this.cursor(e), { passive: true });
          this.track.addEventListener('mouseleave', () => {
            this.mouse = null;
            delete this.track.dataset.cursor;
          });
          this.track.addEventListener('click', (e) => {
            if (!this.track.dataset.cursor || e.target.closest('a, button, video[controls]')) return;
            nav(this.index() + (this.track.dataset.cursor === 'prev' ? -1 : 1));
          });
        }
        this.updateNav(0);
        this.hint();
      }
      updateNav(i) {
        var max = this.dots.length - 1;
        if (this.prev) this.prev.setAttribute('aria-disabled', i <= 0 ? 'true' : 'false');
        if (this.next) this.next.setAttribute('aria-disabled', i >= max ? 'true' : 'false');
        this.thumbs.forEach((t, j) => {
          if (i === j) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }
      cursor(e) {
        this.mouse = e;
        var r = this.track.getBoundingClientRect();
        var dir = e.clientX - r.left < r.width / 2 ? -1 : 1;
        var i = this.index() + dir;
        if (i < 0 || i > this.dots.length - 1) delete this.track.dataset.cursor;
        else this.track.dataset.cursor = dir < 0 ? 'prev' : 'next';
      }
      index() {
        return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      }
      go(i) {
        var n = Math.max(0, Math.min(this.dots.length - 1, i));
        var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: n * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      }
      sync() {
        var pos = this.track.scrollLeft / Math.max(1, this.track.clientWidth);
        var i = Math.round(pos);
        this.dots.forEach((d, j) => {
          d.style.setProperty('--p', Math.max(0, 1 - Math.abs(pos - j)).toFixed(3));
          d.toggleAttribute('data-active', i === j);
        });
        if (i === this.last) return;
        if (this.status) this.status.textContent = (this.status.dataset.template || '').replace('[i]', i + 1);
        this.last = i;
        this.updateNav(i);
        if (this.mouse) this.cursor(this.mouse);
        // Les vidéos hors écran sont mises en pause.
        var slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-slide]'));
        Array.prototype.forEach.call(this.track.querySelectorAll('video'), (v) => {
          if (slides.indexOf(v.closest('[data-slide]')) === i) v.play && v.play().catch(function () {});
          else v.pause && v.pause();
        });
      }
      remember() {
        try {
          sessionStorage.setItem('dreamy:gallery-hint', '1');
        } catch (e) {}
      }
      hint() {
        if (this.dots.length < 2) return;
        try {
          if (sessionStorage.getItem('dreamy:gallery-hint')) return;
        } catch (e) {}
        var run = () =>
          setTimeout(() => {
            var M = window.Dreamy && window.Dreamy.motion;
            var r = this.track.getBoundingClientRect();
            if (this.touched || !M || !M.enabled() || this.track.scrollLeft > 0 || r.bottom < 0 || r.top > window.innerHeight) return;
            this.remember();
            this.track.classList.add('is-hint');
            setTimeout(() => this.track.classList.remove('is-hint'), 900);
          }, 800);
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run, { once: true });
      }
    }
  );
}
